import * as React from "react";
import { cn } from "../../lib/cn";
import type { NavSection } from "./types";

// ── SidebarNavLink — dependency-injected NavLink ──────────────────────────────
// We accept a renderLink render prop so the shell component works with
// any router (react-router, next/link, etc.) without importing it directly.

interface RenderLinkProps {
  to: string;
  className: string | ((props: { isActive: boolean }) => string);
  children: React.ReactNode;
  end?: boolean;
}

export type RenderLinkFn = (props: RenderLinkProps) => React.ReactElement;

// ── SidebarItem ───────────────────────────────────────────────────────────────

interface SidebarItemProps {
  item: import("./types").NavItem;
  renderLink: RenderLinkFn;
}

function SidebarItem({ item, renderLink }: SidebarItemProps) {
  const { to, label, icon: Icon, end, badge } = item;
  return renderLink({
    to,
    end,
    className: ({ isActive }: { isActive: boolean }) =>
      cn(
        "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
        isActive
          ? "bg-indigo-50 text-indigo-700"
          : "text-slate-400 hover:text-white hover:bg-white/10",
      ),
    children: (
      <>
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
        <span className="flex-1 truncate">{label}</span>
        {badge != null && badge > 0 && (
          <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1.5 text-[10px] font-medium text-white">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </>
    ),
  });
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export interface SidebarProps {
  /** App logo/name to display at the top */
  logo?: React.ReactNode;
  /** Context badge text (enterprise slug or "App") */
  contextLabel?: string;
  /** Navigation sections with items */
  sections: NavSection[];
  /** Render prop for router-aware links */
  renderLink: RenderLinkFn;
  /** Footer content (UserMenu) */
  footer?: React.ReactNode;
  className?: string;
}

export function Sidebar({ logo, contextLabel, sections, renderLink, footer, className }: SidebarProps) {
  return (
    <aside className={cn("w-52 flex-shrink-0 flex flex-col bg-[#1e1b4b] min-h-screen", className)}>
      {/* Logo */}
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-white/10 flex-shrink-0">
        {logo ?? (
          <span className="text-white font-semibold text-sm tracking-tight">App</span>
        )}
      </div>

      {/* Context badge */}
      {contextLabel && (
        <div className="px-3 pt-4 pb-2">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10">
            <span className="text-xs text-indigo-200 font-medium truncate capitalize">
              {contextLabel}
            </span>
          </div>
        </div>
      )}

      {/* Nav sections */}
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {sections.map((section) => (
          <React.Fragment key={section.label}>
            <p className="text-xs text-slate-500 uppercase tracking-wider px-3 py-1.5 font-medium mt-3">
              {section.label}
            </p>
            {section.items.map((item) => (
              <SidebarItem key={item.to} item={item} renderLink={renderLink} />
            ))}
          </React.Fragment>
        ))}
      </nav>

      {/* Footer */}
      {footer && (
        <div className="p-3 border-t border-white/10 flex-shrink-0">
          {footer}
        </div>
      )}
    </aside>
  );
}
